import React, { useEffect, useState } from 'react';
import { IARTComponentsFormValues } from 'models/ancillaries';
import { useTranslations } from 'hooks/useTranslations';
import { useForm } from 'react-hook-form';
import { useLang } from 'models/langContext';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { EArtAncillaryType, EArtComponentType } from 'constants/ArtAccessoryType';
import { getFieldsByComponentType } from 'pages/AncillariesPage/helpers';
import Grid from '@mui/material/Grid';
import { StyledSubtitle } from 'pages/AncillariesPage/AncillariesReferenceTablePage/AncillariesReferenceAccesoriessSearchForm/styles';
import { Typography } from '@mui/material';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { StyledAdvancedSearch } from 'pages/AncillariesPage/AncillariesReferenceTablePage/AncillariesReferenceComponentsSearchForm/styles';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { prepareFilters } from 'helpers/filters';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { FormInput } from 'components/molecules/FormInput';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useLocation } from 'react-router-dom';
import { replaceUrlIfOnPage } from 'utils/history';
import { objectToQueryString } from 'helpers/searchQuery';
import { EFirearmType } from 'pages/AncillariesPage/AncillariesReferenceTablePage/ARTComponentsForm/helpers';
import { EFirearmTypeUniqueId } from 'pages/AncillariesPage/AncillariesReferenceTablePage/ARTComponentsForm/helpers';
import { FormAutocomplete } from 'components/molecules/FormAutocomplete';
import { EPerPages, ESortDirectionTypes } from 'models/table';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { FormAsyncMultiAutocomplete } from 'components/molecules/FormAsyncMultiAutocomplete';
import { getFirearmTypesForComponent } from 'utils/ancillaryComponentSearchUtils';

export enum ESearchFormLabels {
  componentTypeUniqueId = 'ancillariesReferenceTable.accordion.component.componentType.label',
  manufacturersUniqueIds = 'ancillariesReferenceTable.accordion.component.componentManufacturer.label',
  productName = 'ancillariesReferenceTable.accordion.productName.label',
  calibresUniqueIds = 'ancillariesReferenceTable.accordion.component.calibre.label',
  length = 'ancillariesReferenceTable.accordion.component.length.label',
  compositionsUniqueIds = 'ancillariesReferenceTable.accordion.component.composition.label',
  capacitiesUniqueIds = 'ancillariesReferenceTable.accordion.component.capacity.label',
  proofHousesUniqueIds = 'ancillariesReferenceTable.accordion.component.proofhouse.label',
  frtTypesUniqueIds = 'ancillariesReferenceTable.accordion.component.firearmType.label',
  frtMakesUniqueIds = 'ancillariesReferenceTable.accordion.component.make.label',
  frtModelsUniqueIds = 'ancillariesReferenceTable.accordion.component.model.label',
  frtManufacturersUniqueIds = 'ancillariesReferenceTable.accordion.component.firearmManufacturer.label',
}

export const initialFormValues: IARTComponentsFormValues = {
  manufacturersUniqueIds: [],
  calibresUniqueIds: [],
  compositionsUniqueIds: [],
  capacitiesUniqueIds: [],
  proofHousesUniqueIds: [],
  frtMakesUniqueIds: [],
  frtModelsUniqueIds: [],
  frtManufacturersUniqueIds: [],
  frtTypesUniqueIds: [],
  componentTypeUniqueId: '',
  productName: '',
  length: '',
  notes: '',
  frtId: '',
  frtLocal: false,
};

interface IAncillariesReferenceComponentsSearchForm {
  onSubmit: (arg: any) => void;
  initialValues: IARTComponentsFormValues;
  isTransactionPage?: boolean;
  type?: string | null;
  setIsQueryAncillariesReferenceLoading: (arg: any) => void;
  isSearchButtonDisabled?: boolean;
  currentPage?: number;
  perPage?: EPerPages;
}

const AncillariesReferenceComponentsSearchForm = ({
  onSubmit,
  initialValues,
  isTransactionPage,
  type = null,
  isSearchButtonDisabled = false,
  setIsQueryAncillariesReferenceLoading,
  perPage,
  currentPage,
}: IAncillariesReferenceComponentsSearchForm) => {
  const { pathname }: any = useLocation();
  const { t } = useTranslations();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [values, setValues] = useState<IARTComponentsFormValues>(initialValues);
  const [isLoadingDictionaries, setIsLoadingDictionaries] = useState<boolean>(false);
  const [isInitialFormLoad, setIsInitialFormLoad] = useState(true);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<IARTComponentsFormValues>({
    defaultValues: initialValues,
  });
  const componentType = watch('componentTypeUniqueId');

  const { selectedLanguage } = useLang();

  const {
    loadDictionaries,
    artComponentType,
    artComponentManufacturer,
    artComponentCalibre,
    artComponentCapacity,
    artComponentProofHouse,
    artAccessoryComposition,
  } = useAncillaries();

  const {
    make,
    model,
    manufacturer,
    firearmType,
    loadDictionaries: loadDictionariesFRT,
  } = useLegalEntities();

  useEffect(() => {
    (async () => {
      setIsLoadingDictionaries(true);
      await loadDictionaries([
        EDictionaryTypes.ArtComponentType,
        EDictionaryTypes.ArtComponentManufacturer,
        EDictionaryTypes.ArtComponentCalibre,
        EDictionaryTypes.ArtComponentCapacity,
        EDictionaryTypes.ArtComponentProofHouse,
        EDictionaryTypes.ArtaccessoryComposition,
      ]);
      await loadDictionariesFRT([
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.Manufacturer,
        EDictionaryTypes.FirearmType,
      ]);
      setIsLoadingDictionaries(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as IARTComponentsFormValues);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    if (!isLoadingDictionaries) {
      setValues(initialValues);
      reset(initialValues);
      handleSubmit(onSubmit)();
    }
  }, [initialValues, isLoadingDictionaries]);

  const componentsTypeChange = (value: EArtComponentType | string) => {
    reset({ ...initialValues, componentTypeUniqueId: value });
    if (value) {
      const fields = getFieldsByComponentType(value as EArtComponentType);
      setAccessFields(fields);
    } else {
      setAccessFields([]);
    }
  };

  useEffect(() => {
    if (initialValues.componentTypeUniqueId) {
      componentsTypeChange(initialValues.componentTypeUniqueId);
    }
  }, [initialValues]);
  const handleResetField = (name?: string, refreshResults = true) => {
    if (name && name !== 'componentTypeUniqueId') {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialValues);
      componentsTypeChange('');
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  const checkDisabled = (fieldName: string) => !accessFields.includes(fieldName);

  const submitForm = (data: IARTComponentsFormValues) => {
    if (data.invokedByPageChange !== true) {
      setIsQueryAncillariesReferenceLoading(true);
    }
    data.invokedByPageChange = undefined;

    if (isTransactionPage) {
      const query = objectToQueryString(data);
      if (query.length) {
        replaceUrlIfOnPage(
          `${pathname}?type=${type}&expand=ancillaries&ancillaryOptionName=${EArtAncillaryType.COMPONENTS}&${query}`,
        );
      } else {
        replaceUrlIfOnPage(
          `${pathname}?type=${type}&expand=ancillaries&ancillaryOptionName=${EArtAncillaryType.COMPONENTS}`,
        );
      }
    } else {
      const query = objectToQueryString({
        ...data,
        perPage,
        currentPage,
      });
      if (query.length) {
        replaceUrlIfOnPage(
          `${pathname}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.COMPONENTS}&${query}`,
        );
      } else {
        replaceUrlIfOnPage(
          `${pathname}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.COMPONENTS}`,
        );
      }
    }

    onSubmit(data);
  };

  useEffect(() => {
    if (!isInitialFormLoad) {
      handleSubmit((val) => submitForm({ ...val, invokedByPageChange: true }))();
    } else {
      setIsInitialFormLoad(false);
    }
  }, [perPage, currentPage]);

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(submitForm)}>
        <StyledAdvancedSearch container columnSpacing={2} columns={10}>
          <Grid size={{ xs: 12 }}>
            <StyledSubtitle size={{ xs: 3 }}>
              <Typography variant="subtitle2">
                {t('ancillaries.accordion.subtitle.label')}
              </Typography>
            </StyledSubtitle>
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(artComponentType)}
              name="componentTypeUniqueId"
              label={t(ESearchFormLabels.componentTypeUniqueId)}
              setValue={(name: string, value: string) => {
                setValue(name, value);
                componentsTypeChange(value as EArtComponentType | '');
              }}
              canBeEmpty
              control={control}
              errors={errors}
              onSelect={componentsTypeChange}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(artComponentManufacturer)}
              name="manufacturersUniqueIds"
              label={t(ESearchFormLabels.manufacturersUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('manufacturersUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="productName"
              label={t(ESearchFormLabels.productName)}
              control={control}
              errors={errors}
              disabled={checkDisabled('productName')}
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtComponentCalibre}
              name="calibresUniqueIds"
              label={t(ESearchFormLabels.calibresUniqueIds)}
              control={control}
              errors={errors}
              disabled={checkDisabled('calibresUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="length"
              label={t(ESearchFormLabels.length)}
              control={control}
              errors={errors}
              disabled={checkDisabled('length')}
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(artAccessoryComposition)}
              name="compositionsUniqueIds"
              label={t(ESearchFormLabels.compositionsUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('compositionsUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(artComponentCapacity)}
              name="capacitiesUniqueIds"
              label={t(ESearchFormLabels.capacitiesUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('capacitiesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(artComponentProofHouse)}
              name="proofHousesUniqueIds"
              label={t(ESearchFormLabels.proofHousesUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('proofHousesUniqueIds')}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <StyledSubtitle size={{ xs: 3 }}>
              <Typography variant="subtitle2">Firearm filters</Typography>
            </StyledSubtitle>
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(
                getFirearmTypesForComponent(firearmType, componentType),
              )}
              name="frtTypesUniqueIds"
              label={t(ESearchFormLabels.frtTypesUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.Make}
              name="frtMakesUniqueIds"
              label={t(ESearchFormLabels.frtMakesUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.Model}
              name="frtModelsUniqueIds"
              label={t(ESearchFormLabels.frtModelsUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.Manufacturer}
              name="frtManufacturersUniqueIds"
              label={t(ESearchFormLabels.frtManufacturersUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid
            size={{ xs: 12 }}
            container
            columns={10}
            columnSpacing={2}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Grid size={{ xs: 10, sm: 10, md: 5, lg: 2 }}>
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                label={t('ancillaries.accordion.search.button')}
                disabled={isSearchButtonDisabled}
                type="submit"
                id="ancillary-reference-table-component-search"
                fullWidth
                sx={{
                  mt: {
                    xs: 2,
                    sm: 3.5,
                  },
                }}
              />
            </Grid>
          </Grid>
        </StyledAdvancedSearch>
      </form>
      <SelectedFilters
        name="ancillaries_reference_components"
        values={values}
        labels={ESearchFormLabels}
        handleDelete={handleResetField}
        type={ESearchFilterKeys.firearm}
        showSaveFilters={false}
        saveQueryParams={['type', 'ancillaryOptionName']}
        dictionaries={{
          componentTypeUniqueId: prepareFilters(artComponentType, 'uniqueId', 'name'),
          manufacturersUniqueIds: prepareFilters(artComponentManufacturer, 'uniqueId', 'name'),
          calibresUniqueIds: prepareFilters(artComponentCalibre, 'uniqueId', 'name'),
          compositionsUniqueIds: prepareFilters(artAccessoryComposition, 'uniqueId', 'name'),
          capacitiesUniqueIds: prepareFilters(artComponentCapacity, 'uniqueId', 'name'),
          proofHousesUniqueIds: prepareFilters(artComponentProofHouse, 'uniqueId', 'name'),
        }}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export default AncillariesReferenceComponentsSearchForm;
